import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';
import { useMatch } from 'react-router-dom';

const PROJECT_SLUG = 'github.com/project-slug';
const SOURCE_LOCATION = 'backstage.io/source-location';

/**
 * When the URL matches `/catalog/:namespace/:kind/:name`, loads the entity for Agent Tasks context.
 */
export function useOptionalEntityFromRoute(): {
  entity: Entity | undefined;
  loading: boolean;
  projectSlug: string | undefined;
  entityRef: string | undefined;
  sourceLocation: string | undefined;
} {
  const catalogApi = useApi(catalogApiRef);
  const match = useMatch('/catalog/:namespace/:kind/:name');

  const { value, loading } = useAsyncRetry(async () => {
    if (
      !match?.params.namespace ||
      !match?.params.kind ||
      !match?.params.name
    ) {
      return undefined;
    }
    const { namespace, kind, name } = match.params;
    return catalogApi.getEntityByRef({ kind, namespace, name });
  }, [
    catalogApi,
    match?.params.namespace,
    match?.params.kind,
    match?.params.name,
  ]);

  const entity = value;
  const projectSlug = entity?.metadata.annotations?.[PROJECT_SLUG];
  const sourceLocation = entity?.metadata.annotations?.[SOURCE_LOCATION];
  const entityRef = entity ? stringifyEntityRef(entity) : undefined;

  return { entity, loading, projectSlug, entityRef, sourceLocation };
}
